/**
 * NAVEGAÇÃO — a definição ÚNICA do menu (MOBILE-COMPLETO, M1).
 *
 * 🔴 Até 22/10/2026 havia DUAS listas mantidas à mão: o menu lateral
 * (`Sidebar.tsx`) e a do celular (`MobileTabBar` + `/mais`). O menu cresceu e a
 * do celular não acompanhou — 13 das 25 telas não tinham caminho nenhum no
 * celular (Leads, Metas, Chamados, Corretiva, Comunicados, Configurações…).
 * ✅ Agora as duas leem DAQUI: o menu lateral desenha estas seções, e a tela
 * "Mais" do celular desenha as MESMAS seções, com as MESMAS permissões. O teste
 * `navegacao.test.ts` reprova se um item do menu lateral não existir no celular
 * — e o `e2e/mobile.spec.ts` tira daqui a lista de rotas que confere no gate,
 * então tela nova entra no teste sozinha.
 *
 * ⚠️ Função PURA: quem lê permissão, flag e workflow é `useNavegacao`. Aqui só
 * se decide O QUE aparece, nunca COMO (o desenho é de cada consumidor).
 * Esconder no menu é conforto; quem barra de verdade é a API (`requireTela`).
 */

export type IconeNav =
  | 'home'
  | 'clientes'
  | 'comercial'
  | 'manutencao'
  | 'projetos'
  | 'compras'
  | 'financeiro'
  | 'comunicados'
  | 'rh'
  | 'ajuda'
  | 'configuracoes';

export interface SubItemNav {
  to: string;
  label: string;
  testId?: string;
  /** Páginas fora do menu que pertencem a esta seção (o seletor do celular as reconhece). */
  tambemEm?: string[];
}

export interface ItemNav {
  /** Identidade estável do item (o teste compara por ela). */
  chave: string;
  to: string;
  label: string;
  icone: IconeNav;
  end?: boolean;
  /** Marca o item como ativo em qualquer rota que comece assim. */
  partialMatch?: string;
  /** Os sub-itens abrem no menu lateral quando a rota começa por um destes. */
  abreEm: string[];
  subItens: SubItemNav[];
  /**
   * Como o menu lateral embrulha o item: `dept` (div sem testid, sub-itens com
   * `sidebar-sub-<slug>`), `bloco` (div com `testIdBloco`) ou `solto` (sem div).
   */
  forma: 'dept' | 'bloco' | 'solto';
  testIdBloco?: string;
  slugDept?: string;
}

export interface SecaoNav {
  chave: 'inicio' | 'operacional' | 'apoio' | 'sistema';
  /** `null` = sem título (a Home). */
  titulo: string | null;
  itens: ItemNav[];
}

export interface WorkflowNav {
  id: string;
  slug: string;
  nome: string;
}

export interface DepartamentoNav {
  slug: string;
  to: string;
  label: string;
}

/**
 * Agrupa workflows ativos por departamento, na ordem. Recebe o formato da API
 * (`/api/v1/workflows`) — é pura para o teste do celular usá-la sem React.
 */
export function agruparWorkflows(
  workflows: Array<{ id: string; slug: string; nome: string; deptSlug: string; ordem: number; ativo: boolean }>,
): Map<string, WorkflowNav[]> {
  const m = new Map<string, Array<WorkflowNav & { ordem: number }>>();
  for (const w of workflows) {
    if (!w.ativo) continue;
    const arr = m.get(w.deptSlug) ?? [];
    arr.push({ id: w.id, slug: w.slug, nome: w.nome, ordem: w.ordem });
    m.set(w.deptSlug, arr);
  }
  for (const arr of m.values()) arr.sort((a, b) => a.ordem - b.ordem);
  return m;
}

/** Os departamentos do grupo Operacional, na ordem do menu. */
export const DEPARTAMENTOS_NAV: DepartamentoNav[] = [
  { slug: 'clientes', to: '/admin/clientes-v2', label: 'Clientes' },
  { slug: 'comercial', to: '/menu/comercial', label: 'Comercial' },
  { slug: 'manutencao', to: '/menu/manutencao', label: 'Manutenção' },
  { slug: 'projetos', to: '/admin/projetos', label: 'Projetos' },
  { slug: 'financeiro', to: '/admin/financeiro/dashboard', label: 'Financeiro' },
];

const ICONE_DO_DEPT: Record<string, IconeNav> = {
  clientes: 'clientes',
  comercial: 'comercial',
  manutencao: 'manutencao',
  projetos: 'projetos',
  financeiro: 'financeiro',
};

/** Tudo que decide o menu — lido por `useNavegacao`, montado à mão no teste. */
export interface EntradaNavegacao {
  /** Já filtrados por departamento + chave (`decidirDepartamentosVisiveis`). */
  departamentosVisiveis: DepartamentoNav[];
  /** Workflows ativos por departamento, já na ordem. */
  workflowsPorDept: Map<string, WorkflowNav[]>;
  crmV2: boolean;
  orcamentoLigado: boolean;
  /** Bloco Comercial para quem não é do dept mas tem chave (orçamentista). */
  mostrarComercialAvulso: boolean;
  comercial: {
    leads: boolean;
    funil: boolean;
    indicadores: boolean;
    metas: boolean;
    orcamentos: boolean;
    excluir: boolean;
  };
  manutencao: { indicadores: boolean; monitoramento: boolean; os: boolean };
  financeiro: { ver: boolean; agir: boolean };
  compras: boolean;
  comunicados: boolean;
  rh: boolean;
  rhLabel: string;
  isAdmin: boolean;
}

function subItensDaManutencao(e: EntradaNavegacao, d: DepartamentoNav): SubItemNav[] {
  // 🔴 Orçamentos NÃO depende da flag do Monitoramento: são capacidades
  // diferentes, e amarrar uma na outra faria a fila de orçamentos sumir no dia
  // em que alguém desligasse o monitoramento.
  const fixos: SubItemNav[] = [
    // OS do sistema (fase 3, mig 772): só aparece para quem tem a chave (no começo, só o admin).
    ...(e.manutencao.os
      ? [
          { to: '/menu/manutencao/agenda', label: 'Agenda', testId: 'sidebar-manutencao-agenda' },
          { to: '/menu/manutencao/chamados-os', label: 'Chamados de OS', testId: 'sidebar-manutencao-chamados-os' },
          { to: '/menu/manutencao/relatorios-os', label: 'Relatórios de OS', testId: 'sidebar-manutencao-relatorios-os' },
          // Achado 12 (Matheus, 28/10): o planejamento das preventivas virou submenu — sai o botão dos Indicadores.
          { to: '/menu/manutencao/planejador', label: 'Planejador', testId: 'sidebar-manutencao-planejador', tambemEm: ['/menu/manutencao/planejar-preventivas'] },
        ]
      : []),
    { to: '/menu/manutencao/orcamentos', label: 'Orçamentos', testId: 'sidebar-manutencao-orcamentos' },
    { to: '/menu/manutencao/chamados-auvo', label: 'Chamados', testId: 'sidebar-manutencao-chamados' },
    // Indicadores (mig 547): a visão de gestão. ⚠️ A aba de indicadores do
    // Monitoramento MIGROU para cá — não existe nos dois lugares.
    ...(e.manutencao.indicadores
      ? [
          {
            to: '/menu/manutencao/indicadores',
            label: 'Indicadores',
            testId: 'sidebar-manutencao-indicadores',
          },
        ]
      : []),
    ...(e.manutencao.monitoramento
      ? [{ to: '/menu/manutencao/monitoramento', label: 'Monitoramento', testId: 'sidebar-manutencao-monitoramento' }]
      : []),
  ];
  // ⚠️ Na Manutenção o fluxo aparece MESMO sendo um só (mig 802: sobrou a Corretiva, onde caem os chamados do Portal): o
  // item do departamento agora leva à Agenda, não ao fluxo, então sem o subitem ninguém chegaria nela.
  return [...fixos, ...subItensDeWorkflow(e, d, 1)];
}

function subItensDeWorkflow(e: EntradaNavegacao, d: DepartamentoNav, piso = 2): SubItemNav[] {
  const wfs = e.workflowsPorDept.get(d.slug) ?? [];
  // O piso de 2 workflows: com um só, o item do departamento já leva a ele.
  if (wfs.length < piso) return [];
  return wfs.map((w) => ({ to: `${d.to}/${w.slug}`, label: w.nome, testId: `sidebar-sub-${d.slug}-${w.slug}` }));
}

function itemDept(e: EntradaNavegacao, d: DepartamentoNav): ItemNav {
  return {
    chave: d.slug,
    to: d.to,
    label: d.label,
    icone: ICONE_DO_DEPT[d.slug] ?? 'home',
    abreEm: [d.to],
    subItens: d.slug === 'manutencao' ? subItensDaManutencao(e, d) : subItensDeWorkflow(e, d),
    forma: 'dept',
    slugDept: d.slug,
  };
}

function itemComercial(e: EntradaNavegacao, d: DepartamentoNav, testIdBloco: string): ItemNav {
  const c = e.comercial;
  const subItens: SubItemNav[] = [
    ...(c.leads ? [{ to: '/admin/comercial/leads', label: 'Leads', testId: 'sidebar-comercial-leads' }] : []),
    ...(c.funil ? [{ to: '/admin/comercial/funil', label: 'Funil', testId: 'sidebar-comercial-funil' }] : []),
    ...(c.indicadores
      ? [{ to: '/admin/comercial/indicadores', label: 'Indicadores', testId: 'sidebar-comercial-indicadores' }]
      : []),
    ...(c.metas ? [{ to: '/admin/comercial/metas', label: 'Metas', testId: 'sidebar-comercial-metas' }] : []),
    ...(e.orcamentoLigado && c.orcamentos
      ? [{ to: '/admin/comercial/orcamentos', label: 'Orçamentos', testId: 'sidebar-comercial-orcamentos' }]
      : []),
    // 🔴 BLOCO L: a PORTA da lista de exclusões — a chave é a MESMA que
    // autoriza excluir (quem pode excluir é quem vê o que foi excluído).
    ...(c.excluir
      ? [{ to: '/admin/comercial/exclusoes', label: 'Exclusões', testId: 'sidebar-comercial-exclusoes' }]
      : []),
  ];
  return {
    chave: 'comercial',
    to: '/admin/comercial/funil',
    label: d.label,
    icone: 'comercial',
    partialMatch: '/admin/comercial',
    abreEm: ['/admin/comercial', '/menu/comercial/orcamentos'],
    subItens,
    forma: 'bloco',
    testIdBloco,
  };
}

function itemFinanceiro(e: EntradaNavegacao, d: DepartamentoNav): ItemNav {
  // S-4: os sub-itens seguem as MESMAS chaves que o AdminLayout exige para
  // abrir cada rota (`acesso-rotas.ts`).
  return {
    chave: 'financeiro',
    to: d.to,
    label: d.label,
    icone: 'financeiro',
    partialMatch: '/admin/financeiro',
    abreEm: ['/admin/financeiro'],
    subItens: [
      ...(e.financeiro.ver
        ? [{ to: '/admin/financeiro/dashboard', label: 'Indicadores', testId: 'sidebar-financeiro-indicadores' }]
        : []),
      ...(e.financeiro.agir
        ? [{ to: '/admin/financeiro/simulador', label: 'Simulador', testId: 'sidebar-financeiro-simulador' }]
        : []),
    ],
    forma: 'bloco',
    testIdBloco: 'sidebar-financeiro',
  };
}

const ITEM_COMPRAS: ItemNav = {
  chave: 'compras',
  to: '/menu/compras/quadro',
  label: 'Compras',
  icone: 'compras',
  partialMatch: '/menu/compras',
  abreEm: ['/menu/compras'],
  subItens: [
    { to: '/menu/compras/quadro', label: 'Quadro', testId: 'sidebar-compras-quadro' },
    { to: '/menu/compras/cotacoes', label: 'Cotações', testId: 'sidebar-compras-cotacoes' },
    { to: '/menu/compras/solicitar', label: 'Solicitar', testId: 'sidebar-compras-solicitar' },
  ],
  forma: 'bloco',
  testIdBloco: 'sidebar-compras',
};

const solto = (chave: string, to: string, label: string, icone: IconeNav, extra: Partial<ItemNav> = {}): ItemNav => ({
  chave,
  to,
  label,
  icone,
  abreEm: [],
  subItens: [],
  forma: 'solto',
  ...extra,
});

export function montarNavegacao(e: EntradaNavegacao): SecaoNav[] {
  const comercialDept = DEPARTAMENTOS_NAV.find((d) => d.slug === 'comercial');
  const operacional: ItemNav[] = [
    ...e.departamentosVisiveis
      .filter((d) => d.slug !== 'financeiro')
      .map((d) =>
        d.slug === 'comercial' && e.crmV2 ? itemComercial(e, d, 'sidebar-comercial-v2') : itemDept(e, d),
      ),
    ...(e.mostrarComercialAvulso && comercialDept
      ? [itemComercial(e, comercialDept, 'sidebar-comercial-orcamentista')]
      : []),
    // FX-9: Compras dentro de Operacional, ACIMA de Financeiro.
    ...(e.compras ? [ITEM_COMPRAS] : []),
    ...e.departamentosVisiveis.filter((d) => d.slug === 'financeiro').map((d) => itemFinanceiro(e, d)),
  ];

  const secoes: SecaoNav[] = [
    { chave: 'inicio', titulo: null, itens: [solto('home', '/', 'Home', 'home', { end: true })] },
    { chave: 'operacional', titulo: 'Operacional', itens: operacional },
    {
      chave: 'apoio',
      titulo: 'Apoio',
      itens: [
        // Comunicados (mig 542): item PRÓPRIO — é a empresa falando com os clientes.
        ...(e.comunicados ? [solto('comunicados', '/comunicados', 'Comunicados', 'comunicados')] : []),
        ...(e.rh ? [solto('rh', '/rh', e.rhLabel, 'rh')] : []),
        solto('ajuda', '/ajuda', 'Ajuda', 'ajuda'),
      ],
    },
  ];
  if (e.isAdmin) {
    secoes.push({
      chave: 'sistema',
      titulo: 'Sistema',
      itens: [
        solto('configuracoes', '/admin/configuracoes', 'Configurações', 'configuracoes', { partialMatch: '/admin' }),
      ],
    });
  }
  return secoes;
}

/**
 * Todas as rotas que o menu oferece (itens + sub-itens), sem repetição — é a
 * lista que o teste do celular confere.
 */
export function rotasDaNavegacao(secoes: SecaoNav[]): string[] {
  const rotas = new Set<string>();
  for (const s of secoes) {
    for (const i of s.itens) {
      rotas.add(i.to);
      for (const sub of i.subItens) rotas.add(sub.to);
    }
  }
  return [...rotas];
}

/**
 * O item (e o sub-item) da navegação a que uma rota pertence — é o que o
 * seletor de seção do celular mostra ("Manutenção: Corretiva ▾").
 */
export function secaoDaRota(
  secoes: SecaoNav[],
  pathname: string,
): { item: ItemNav; sub: SubItemNav | null } | null {
  let melhor: { item: ItemNav; sub: SubItemNav | null; tamanho: number } | null = null;
  for (const s of secoes) {
    for (const item of s.itens) {
      if (item.subItens.length === 0) continue;
      const doItem = item.abreEm.some((p) => pathname.startsWith(p));
      for (const sub of item.subItens) {
        const casa = [sub.to, ...(sub.tambemEm ?? [])].some((p) => pathname === p || pathname.startsWith(`${p}/`));
        if (casa) {
          if (!melhor || sub.to.length > melhor.tamanho) melhor = { item, sub, tamanho: sub.to.length };
        }
      }
      if (doItem && !melhor) melhor = { item, sub: null, tamanho: 0 };
    }
  }
  return melhor ? { item: melhor.item, sub: melhor.sub } : null;
}
